import type { SupabaseClient } from "@supabase/supabase-js";

export type FinanceTransactionKind = "sale" | "purchase" | "expense";
export type FinancePaymentMethod = "cash" | "card_transfer" | "bank_transfer";

export interface FinanceCounterparty {
  id: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  note?: string;
  isCustomer: boolean;
  isVendor: boolean;
  isActive: boolean;
  domainVersion: number;
}

export interface FinanceTransaction {
  id: string;
  transactionCode: string;
  transactionKind: FinanceTransactionKind;
  categoryCode: string;
  description: string;
  amountToman: number;
  paidAmountToman: number;
  remainingAmountToman: number;
  quantity?: number;
  unit?: string;
  tankId?: string;
  counterpartyId?: string;
  occurredOn: string;
  status: "posted" | "voided";
  domainVersion: number;
  voidReason?: string;
  createdAt: string;
}

export interface FinancePayment {
  id: string;
  transactionId: string;
  amountToman: number;
  paymentMethod: FinancePaymentMethod;
  paidOn: string;
  note?: string;
  createdAt: string;
}

export interface FinanceManagementSnapshot {
  counterparties: FinanceCounterparty[];
  transactions: FinanceTransaction[];
  payments: FinancePayment[];
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function rows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record) : [];
}

function numberValue(value: unknown): number {
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function transactionKind(value: unknown): FinanceTransactionKind {
  if (value === "sale" || value === "purchase" || value === "expense") return value;
  throw new Error("FINANCE_TRANSACTION_KIND_INVALID");
}

function paymentMethod(value: unknown): FinancePaymentMethod {
  if (value === "cash" || value === "card_transfer" || value === "bank_transfer") return value;
  throw new Error("FINANCE_PAYMENT_METHOD_INVALID");
}

export async function loadFinanceManagementSnapshot(
  client: SupabaseClient,
  farmId: string,
): Promise<FinanceManagementSnapshot> {
  const { data, error } = await client.rpc("finance_admin_snapshot_v1", {
    target_farm_id: farmId,
  });
  if (error) throw error;

  const root = record(data);

  return {
    counterparties: rows(root.counterparties).map((row): FinanceCounterparty => {
      const firstName = optionalString(row.firstName);
      const lastName = optionalString(row.lastName);
      const phone = optionalString(row.phone);
      const note = optionalString(row.note);

      return {
        id: String(row.id),
        ...(firstName ? { firstName } : {}),
        ...(lastName ? { lastName } : {}),
        ...(phone ? { phone } : {}),
        ...(note ? { note } : {}),
        isCustomer: row.isCustomer === true,
        isVendor: row.isVendor === true,
        isActive: row.isActive === true,
        domainVersion: numberValue(row.domainVersion),
      };
    }),
    transactions: rows(root.transactions).map((row): FinanceTransaction => {
      const unit = optionalString(row.unit);
      const tankId = optionalString(row.tankId);
      const counterpartyId = optionalString(row.counterpartyId);
      const voidReason = optionalString(row.voidReason);

      return {
        id: String(row.id),
        transactionCode: String(row.transactionCode ?? ""),
        transactionKind: transactionKind(row.transactionKind),
        categoryCode: String(row.categoryCode ?? ""),
        description: String(row.description ?? ""),
        amountToman: numberValue(row.amountToman),
        paidAmountToman: numberValue(row.paidAmountToman),
        remainingAmountToman: numberValue(row.remainingAmountToman),
        ...(row.quantity === null || row.quantity === undefined
          ? {}
          : { quantity: numberValue(row.quantity) }),
        ...(unit ? { unit } : {}),
        ...(tankId ? { tankId } : {}),
        ...(counterpartyId ? { counterpartyId } : {}),
        occurredOn: String(row.occurredOn ?? ""),
        status: row.status === "voided" ? "voided" : "posted",
        domainVersion: numberValue(row.domainVersion),
        ...(voidReason ? { voidReason } : {}),
        createdAt: String(row.createdAt ?? ""),
      };
    }),
    payments: rows(root.payments).map((row): FinancePayment => {
      const note = optionalString(row.note);

      return {
        id: String(row.id),
        transactionId: String(row.transactionId),
        amountToman: numberValue(row.amountToman),
        paymentMethod: paymentMethod(row.paymentMethod),
        paidOn: String(row.paidOn ?? ""),
        ...(note ? { note } : {}),
        createdAt: String(row.createdAt ?? ""),
      };
    }),
  };
}

export function counterpartyDisplayName(counterparty: FinanceCounterparty): string {
  return [counterparty.firstName, counterparty.lastName].filter(Boolean).join(" ") || "طرف حساب";
}

export function financePaymentMethodLabel(method: FinancePaymentMethod): string {
  if (method === "cash") return "نقدی";
  if (method === "card_transfer") return "کارت‌به‌کارت";
  return "انتقال بانکی";
}

export function financeTransactionKindLabel(kind: FinanceTransactionKind): string {
  if (kind === "sale") return "فروش ماهی";
  if (kind === "purchase") return "خرید";
  return "هزینه";
}
